import React, { createContext, useCallback, useContext, useState } from 'react'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  ts: string
  agentName?: string
  status?: 'thinking' | 'answering' | 'approving' | 'done'
  eq_score?: number
  message_type?: 'normal' | 'proactive' | 'briefing' | 'alert' | 'system'
}

interface ChatContextType {
  chat: ChatMessage[]
  addMessage: (message: ChatMessage) => void
  updateLastMessage: (updates: Partial<ChatMessage>) => void
  clearChat: () => void
  loadHistory: (messages: ChatMessage[]) => void
  historyLoaded: boolean
  inputMention: string
  setInputMention: (text: string) => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

const INITIAL_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: 'Ready. Type a message or use /commands.',
  ts: new Date().toLocaleTimeString(),
  agentName: 'My Agent',
  status: 'done'
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [chat, setChat] = useState<ChatMessage[]>([INITIAL_MESSAGE])
  const [inputMention, setInputMention] = useState('')
  const [historyLoaded, setHistoryLoaded] = useState(false)

  const addMessage = (message: ChatMessage) => {
    setChat(prev => [...prev, message])
  }

  const updateLastMessage = (updates: Partial<ChatMessage>) => {
    setChat(prev => {
      if (prev.length === 0) return prev
      const updated = [...prev]
      updated[updated.length - 1] = { ...updated[updated.length - 1], ...updates }
      return updated
    })
  }

  const clearChat = () => {
    setChat([INITIAL_MESSAGE])
    setHistoryLoaded(false)
  }

  const loadHistory = useCallback((messages: ChatMessage[]) => {
    if (messages.length > 0) {
      // Replace initial placeholder with real history
      setChat([...messages])
    }
    // Always mark as loaded — even if empty, so we don't retry
    setHistoryLoaded(true)
  }, [])

  return (
    <ChatContext.Provider value={{ chat, addMessage, updateLastMessage, clearChat, loadHistory, historyLoaded, inputMention, setInputMention }}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChat() {
  const context = useContext(ChatContext)
  if (!context) throw new Error('useChat must be used within ChatProvider')
  return context
}
